import type { RichText } from "@storytell/cp-client";
import {
  type PmMark,
  type PmText,
  type PmInline,
  type PmParagraph,
  type PmNode,
  type PmDoc,
  type PmHeading,
  type PmBulletList,
  type PmOrderedList,
  type PmListItem,
  type PmBlockquote,
  type PmCodeBlock,
  type PmTable,
  type PmTableRow,
  type PmTableCell,
  type PmHorizontalRule,
  RichTextType,
  PmMarkType,
  PmNodeType,
  type PmTableHeader,
} from "@storytell/types/richText";

const parseMark = (mark: PmMark): RichText.Mark | undefined => {
  switch (mark.type) {
    case PmMarkType.Bold:
      return { type: RichTextType.BoldType } as RichText.Mark;
    case PmMarkType.Italic:
      return { type: RichTextType.ItalicType } as RichText.Mark;
    case PmMarkType.Strike:
      return { type: RichTextType.StrikeType } as RichText.Mark;
    case PmMarkType.Code:
      return { type: RichTextType.CodeType } as RichText.Mark;
    case PmMarkType.Link:
      return {
        type: RichTextType.LinkType,
        attrs: {
          case: "linkAttrs",
          value: { href: mark.attrs.href, title: "" },
        },
      } as RichText.Mark;
  }
};

const parseText = (node: PmText): RichText.Node =>
  ({
    type: RichTextType.TextType,
    text: node.text,
    marks: (node.marks?.map(parseMark).filter(Boolean) as RichText.Mark[]) || [],
  }) as RichText.Node;

const parseInlineNodes = (content: PmInline[]): RichText.Node[] =>
  content
    .map((node) => {
      switch (node.type) {
        case PmNodeType.Text:
          return parseText(node);
        case PmNodeType.VariableV1:
          return parseVariable(node);
      }
    })
    .filter(Boolean) as RichText.Node[];

const parseVariable = (node: PmNode) => {
  if (node.type !== PmNodeType.VariableV1) return undefined;

  return {
    type: RichTextType.MustacheVariableType,
    attrs: {
      case: "variableAttrs",
      value: { value: node.attrs.id },
    },
  } as RichText.Node;
};

const parseParagraph = (node: PmParagraph) => {
  return {
    type: RichTextType.ParagraphType,
    content: parseInlineNodes(node.content),
  } as RichText.Node;
};

const parseHeading = (node: PmHeading): RichText.Node =>
  ({
    type: RichTextType.HeadingType,
    attrs: {
      case: "headingAttrs",
      value: { level: node.attrs.level },
    },
    content: parseInlineNodes(node.content),
  }) as RichText.Node;

const parseListItem = (node: PmListItem): RichText.Node =>
  ({
    type: RichTextType.ListItemType,
    attrs: {
      case: "listItemAttrs",
      value: {
        level: 1,
        checked: undefined, // TODO: Extract from task list structure if needed
      },
    },
    content: parseBlockNodes(node.content),
  }) as unknown as RichText.Node;

const parseList = (node: PmBulletList | PmOrderedList): RichText.Node => {
  const listType = node.type === PmNodeType.OrderedList ? "ordered" : "bullet";
  return {
    type: RichTextType.ListType,
    attrs: {
      case: "listAttrs",
      value: {
        listType,
        start: listType === "ordered" ? 1 : undefined,
      },
    },
    content: node.content.map(parseListItem),
  } as RichText.Node;
};

const parseBlockquote = (node: PmBlockquote): RichText.Node =>
  ({
    type: RichTextType.BlockquoteType,
    content: parseBlockNodes(node.content),
  }) as RichText.Node;

const parseCodeBlock = (node: PmCodeBlock): RichText.Node =>
  ({
    type: RichTextType.CodeBlockType,
    attrs: {
      case: "codeBlockAttrs",
      value: { language: node.attrs?.language || "" },
    },
    content: node.content.map(parseText),
  }) as RichText.Node;

const parseTableCell = (
  node: PmTableCell | PmTableHeader,
  options?: { forceCellType?: "tableCell" },
): RichText.Node => {
  const isHeader = node.type === PmNodeType.TableHeader && options?.forceCellType !== "tableCell";
  return {
    type: isHeader ? RichTextType.TableHeaderType : RichTextType.TableCellType,
    attrs: node.attrs
      ? {
          case: "tableCellAttrs",
          value: {
            colspan: node.attrs.colspan || 1,
            rowspan: node.attrs.rowspan || 1,
            alignment: node.attrs.alignment || "left",
          },
        }
      : undefined,
    content: node.content
      .flatMap((contentNode) => {
        // If it's a paragraph, extract its inline content
        if (contentNode.type === PmNodeType.Paragraph) {
          return parseInlineNodes(contentNode.content);
        }
        // Handle other content types
        const blockNodes = parseBlockNodes([contentNode]);
        return blockNodes.length > 0 ? blockNodes[0] : undefined;
      })
      .filter(Boolean) as RichText.Node[],
  } as RichText.Node;
};

const parseTableRow = (node: PmTableRow): RichText.Node => {
  return {
    type: RichTextType.TableRowType,
    content: node.content.map(parseTableCell),
  } as RichText.Node;
};

const parseTable = (node: PmTable): RichText.Node => {
  // Count rows and columns
  const rows = node.content.length;
  const cols = rows > 0 && node.content[0] ? node.content[0].content.length : 0;

  // Check if first row contains header cells
  const hasHeaderRow =
    rows > 0 && node.content[0] && node.content[0].content.every((cell) => cell.type === PmNodeType.TableHeader);

  // Convert table rows to the correct structure
  const convertedContent: RichText.Node[] = [];

  for (let i = 0; i < node.content.length; i++) {
    const row = node.content[i];
    if (!row) continue;

    if (i === 0 && hasHeaderRow) {
      // First row with header cells should be tableHeader, but children should be tableCell
      const headerCells: RichText.Node[] = [];
      for (const cell of row.content as (PmTableCell | PmTableHeader)[]) {
        headerCells.push(parseTableCell(cell, { forceCellType: "tableCell" }));
      }
      convertedContent.push({
        type: RichTextType.TableHeaderType,
        content: headerCells,
      } as RichText.Node);
    } else {
      // Regular rows should be tableRow
      const rowCells: RichText.Node[] = [];
      for (const cell of row.content as (PmTableCell | PmTableHeader)[]) {
        rowCells.push(parseTableCell(cell));
      }
      convertedContent.push({
        type: RichTextType.TableRowType,
        content: rowCells,
      } as RichText.Node);
    }
  }

  return {
    type: RichTextType.TableType,
    attrs: {
      case: "tableAttrs",
      value: {
        rows,
        cols,
        headerRow: hasHeaderRow,
      },
    },
    content: convertedContent,
  } as RichText.Node;
};

const parseHorizontalRule = (node: PmHorizontalRule): RichText.Node =>
  ({
    type: RichTextType.HorizontalRuleType,
    attrs: node.attrs
      ? {
          case: "hrAttrs",
          value: { style: node.attrs.style || "solid" },
        }
      : undefined,
  }) as RichText.Node;

const parseBlockNodes = (content: PmNode[]): RichText.Node[] =>
  content
    .map((node) => {
      switch (node.type) {
        case PmNodeType.VariableV1:
          return parseVariable(node);
        case PmNodeType.Paragraph:
          return parseParagraph(node);
        case PmNodeType.Heading:
          return parseHeading(node);
        case PmNodeType.BulletList:
        case PmNodeType.OrderedList:
          return parseList(node);
        case PmNodeType.Blockquote:
          return parseBlockquote(node);
        case PmNodeType.CodeBlock:
          return parseCodeBlock(node);
        case PmNodeType.Table:
          return parseTable(node);
        case PmNodeType.HorizontalRule:
          return parseHorizontalRule(node);
      }

      return undefined as unknown as RichText.Node;
    })
    .filter(Boolean) as RichText.Node[];

export const proseMirrorToRichText = (pmDoc: PmDoc): RichText.RichText =>
  ({
    type: RichTextType.DocumentType,
    content: parseBlockNodes(pmDoc.content),
  }) as RichText.RichText;
